import { useState, useCallback, useRef } from 'react'
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  type QueryFunctionContext,
  type PlaceholderDataFunction,
} from '@tanstack/react-query'

export interface PaginatedResponse<T> {
  data: T[]
  max_page: number
  has_next: boolean
  count: number
}

export interface QueryParams {
  page: number
  pageSize: number
  search: string
  filters: Record<string, string[]>
  fields?: string
  expand?: string
  ordering?: string
  [key: string]: unknown
}

export interface UseTableStateOptions<T> {
  queryKey: string
  queryFn: (params: QueryParams) => Promise<PaginatedResponse<T>>
  itemsPerPage?: number
  fields?: string
  expand?: string
  enabled?: boolean
  staleTime?: number
  placeholderData?: T[] | PlaceholderDataFunction<T[]>
  extraParams?: Record<string, unknown>
  initialPage?: number
  initialFilters?: Record<string, string[]>
}

export function useTableState<T = Record<string, unknown>>({
  queryKey,
  queryFn,
  itemsPerPage = 15,
  fields,
  expand,
  enabled = true,
  staleTime = 30 * 1000,
  placeholderData,
  extraParams = {},
  initialPage = 1,
  initialFilters = {},
}: UseTableStateOptions<T>) {
  const [page, setPageRaw] = useState(initialPage)
  const [search, setSearchRaw] = useState('')
  const [filters, setFiltersRaw] = useState<Record<string, string[]>>(initialFilters)
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([])
  const [sortBy, setSortByRaw] = useState<string>('')

  const queryClient = useQueryClient()
  const extraParamsRef = useRef(extraParams)
  extraParamsRef.current = extraParams

  const setPage = useCallback((value: number) => {
    setPageRaw(Math.max(1, value))
  }, [])

  const setSearch = useCallback((value: string) => {
    setSearchRaw(value)
    setPageRaw(1)
  }, [])

  const setSortBy = useCallback((value: string) => {
    setSortByRaw(value)
    setPageRaw(1)
  }, [])

  const setFilters = useCallback((value: Record<string, string[]>) => {
    setFiltersRaw(value)
    setPageRaw(1)
  }, [])

  const updateFilter = useCallback((key: string, values: string[]) => {
    setFiltersRaw((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPageRaw(1)
  }, [])

  const handleSelectRow = useCallback((id: string | number, isChecked: boolean) => {
    setSelectedRows((prev) =>
      isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)
    )
  }, [])

  const handleSelectAll = useCallback((isChecked: boolean, ids: (string | number)[]) => {
    setSelectedRows(isChecked ? ids : [])
  }, [])

  const clearSelection = useCallback(() => setSelectedRows([]), [])

  const queryParams: QueryParams = {
    page,
    pageSize: itemsPerPage,
    search,
    filters,
    fields,
    expand,
    ordering: sortBy || undefined,
    ...extraParamsRef.current,
  }

  const query = useQuery({
    queryKey: [queryKey, page, search, filters, sortBy, extraParamsRef.current],
    queryFn: () => queryFn(queryParams),
    staleTime,
    enabled,
    placeholderData: placeholderData as any,
  })

  const prefetchNextPage = useCallback(() => {
    const currentCount = (query.data?.data ?? []).length
    if (currentCount < itemsPerPage) return
    const nextPage = page + 1
    const maxPage = query.data?.max_page ?? 1
    if (nextPage > maxPage) return

    queryClient.prefetchQuery({
      queryKey: [queryKey, nextPage, search, filters, sortBy, extraParamsRef.current],
      queryFn: () =>
        queryFn({
          page: nextPage,
          pageSize: itemsPerPage,
          search,
          filters,
          fields,
          expand,
          ordering: sortBy || undefined,
          ...extraParamsRef.current,
        }),
      staleTime,
    })
  }, [query.data, page, search, filters, sortBy, itemsPerPage, queryClient, queryKey, fields, expand, staleTime])

  return {
    search,
    setSearch,
    filters,
    setFilters,
    updateFilter,
    page,
    setPage,
    itemsPerPage,
    sortBy,
    setSortBy,
    data: (query.data?.data ?? []) as T[],
    totalPages: query.data?.max_page ?? 1,
    hasMore: query.data?.has_next ?? false,
    totalItems: query.data?.count ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    selectedRows,
    handleSelectRow,
    handleSelectAll,
    clearSelection,
    query,
    prefetchNextPage,
    queryParams,
    queryKey: [queryKey, page, search, filters, sortBy, extraParamsRef.current] as const,
  }
}

/* ─── useTableFilter ───────────────────────────────────────────────
   Helper for filter dropdowns that need infinite-scroll data.
   Wraps useInfiniteQuery with the standard pageParam pattern.
──────────────────────────────────────────────────────────────────── */

interface UseTableFilterOptions<T> {
  queryKey: string | string[]
  queryFn: (ctx: QueryFunctionContext) => Promise<PaginatedResponse<T>>
  search?: string
  enabled?: boolean
  staleTime?: number
  pageSize?: number
}

export function useTableFilter<T = Record<string, unknown>>({
  queryKey,
  queryFn,
  search = '',
  enabled = true,
  staleTime = 30 * 1000,
  pageSize = 10,
}: UseTableFilterOptions<T>) {
  const infiniteQuery = useInfiniteQuery({
    queryKey: Array.isArray(queryKey) ? [...queryKey, pageSize, search] : [queryKey, pageSize, search],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<T>, allPages: PaginatedResponse<T>[]) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
    enabled,
    staleTime,
  })

  const flatList = infiniteQuery.data?.pages.flatMap((page) => page.data) ?? []

  return {
    data: flatList,
    fetchNextPage: infiniteQuery.fetchNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    refetch: infiniteQuery.refetch,
  }
}

/* ─── useTableView ─────────────────────────────────────────────────
   For tables that switch between views (e.g. active/disabled tabs).
   Manages per-view filter overrides so switching tabs resets page.
──────────────────────────────────────────────────────────────────── */

interface ViewConfig {
  key: string
  filters: Record<string, string[]>
}

export function useTableView(views: ViewConfig[], initialView?: string) {
  const [activeView, setActiveViewRaw] = useState(initialView ?? views[0]?.key ?? '')

  const currentView = views.find((v) => v.key === activeView) ?? views[0]

  const setActiveView = useCallback(
    (key: string) => {
      setActiveViewRaw(key)
    },
    []
  )

  return {
    activeView,
    setActiveView,
    viewFilters: currentView?.filters ?? {},
  }
}

export type TableState<T = Record<string, unknown>> = ReturnType<typeof useTableState<T>>
